import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Boxes, Plus, ChevronRight, Package } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STANDARD_COLORS = {
  ERC721: theme.colors.neon.blue,
  ERC1155: theme.colors.accent.teal,
};

const NftProjectsList = ({ user }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/nft/projects`);
      setProjects(res.data.projects || []);
    } catch (err) {
      console.error('Error fetching NFT projects:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading projects...</div>;
  }

  return (
    <div data-testid="nft-projects-list-page" style={{ padding: '40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
        <div>
          <h1 data-testid="nft-projects-title" style={{
            fontFamily: theme.fonts.heading, fontSize: '28px', fontWeight: 700,
            color: theme.colors.text.primary, marginBottom: '6px'
          }}>
            NFT Metadata Projects
          </h1>
          <p style={{ fontSize: '14px', color: theme.colors.text.secondary }}>
            Build metadata packs for your deployed smart contracts
          </p>
        </div>
        <button
          data-testid="create-nft-project-btn"
          onClick={() => navigate('/creator/nft/projects/new')}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 20px', borderRadius: theme.radius.cta,
            background: theme.colors.primary, color: '#fff',
            border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600
          }}
        >
          <Plus size={16} /> New Project
        </button>
      </div>

      {projects.length === 0 ? (
        <div style={{
          background: theme.colors.bg.secondary, borderRadius: theme.radius.card,
          padding: '60px 40px', textAlign: 'center', border: `1px solid ${theme.colors.borders}`
        }}>
          <Boxes size={48} style={{ color: theme.colors.text.muted, marginBottom: '16px' }} />
          <p style={{ color: theme.colors.text.secondary, fontSize: '15px', marginBottom: '20px' }}>
            No NFT metadata projects yet. Create one linked to a deployed contract.
          </p>
          <button
            onClick={() => navigate('/creator/nft/projects/new')}
            style={{
              padding: '10px 24px', borderRadius: theme.radius.cta,
              background: theme.colors.primary, color: '#fff',
              border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600
            }}
          >
            Create First Project
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {projects.map((p) => {
            const color = STANDARD_COLORS[p.standard] || theme.colors.primary;
            return (
              <div
                key={p.id}
                data-testid={`nft-project-row-${p.id}`}
                onClick={() => navigate(`/creator/nft/projects/${p.id}`)}
                style={{
                  background: theme.colors.bg.card, borderRadius: theme.radius.sm,
                  border: `1px solid ${theme.colors.borders}`, padding: '18px 22px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '16px',
                  transition: 'all 0.15s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = theme.colors.bg.secondary}
                onMouseOut={(e) => e.currentTarget.style.background = theme.colors.bg.card}
              >
                <div style={{
                  width: '42px', height: '42px', borderRadius: '12px',
                  background: `${color}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <Package size={20} style={{ color }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary }}>{p.name}</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600,
                      background: `${color}12`, color
                    }}>{p.standard}</span>
                    {p.items_generated && (
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600,
                        background: `${theme.colors.status.success}15`, color: theme.colors.status.success
                      }}>Items Ready</span>
                    )}
                  </div>
                  <span style={{ fontSize: '12px', color: theme.colors.text.muted }}>
                    {p.quantity} items &middot; {p.chain} &middot; {new Date(p.created_at).toLocaleDateString()}
                  </span>
                </div>
                <ChevronRight size={16} style={{ color: theme.colors.text.muted }} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NftProjectsList;
